import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { GetDay, GetShip, GetShipContext } from "../../App/Context"
import { AppViewData } from "../App/Context"
import { logger } from "../../App/Logger"
import { Loading } from "../../Components/Loading"
import { TabBarTabProps } from "../../Components/TabBar"
import { IAppState, INote, INoteParentType, INoteType } from "../../Types"
import { DataStore, nestedNoteRows, useDataStore, useQuery, useRetainedEntities } from "../../Data"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { GetShipView } from "../ShipView"
import { autoCreateNoteTypes, GetNoteParent, NoteOwnedByParent, NoteParent, NoteParentTypeConfiguration, NoteTypeIcon, singleNoteTypes } from "./Context"
import { NoteView } from "./NoteView"
import { NotesViewProps } from "./Params"
import { useNoteWriters } from "./writers"

export function NoteTabs(app: IAppState, parentType: INoteParentType, notes: INote[]): TabBarTabProps[] {
  const t = app.getT("NoteView");
  const config = NoteParentTypeConfiguration[parentType];
  return config.types.map((type) => {
    const typeName = INoteType[type];
    return {
      icon: NoteTypeIcon(type),
      name: `note-${typeName}`,
      title: t(typeName),
      render: () => <NotesView key={`note-${typeName}`} notes={notes.filter((n) => n.noteType == type)} parentType={parentType} noteType={type} />,
    };
  });
  return [];
}

export function NotesView({ notes, noteType, parentType }: NotesViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const contextStore = useDataStore() as DataStore | undefined;
  const [fallbackStore] = useState(() => new DataStore());
  const store = contextStore ?? fallbackStore;
  const writers = useNoteWriters(ship.id);
  useState(() => {
    store.applyBundle({ entities: nestedNoteRows(notes) });
    return true;
  });
  const { apiUser } = GetShipContext();
  const { shipCloudEditable } = GetShipView();
  const t = app.getT("NoteView");
  const [cruise, trip, day] = GetNoteParent(parentType);
  // Read unconditionally (mirrors GetNoteParent's own rationale) so every note
  // rendered here shares this instance's single current/historical state, keyed off
  // whichever entity most specifically owns it (day's trip / trip / cruise) — matches
  // the server's `isCurrentNote` precedence (LogbookApi/NoteApi.swift).
  const dayIsCurrentTrip = GetDay()?.isCurrentTrip;
  const [autoCreatingNote, setAutoCreatingNote] = useState<number>(0);
  const isCurrentTrip = useMemo(() => {
    switch (parentType) {
      case INoteParentType.day:
        return dayIsCurrentTrip ?? true;
      case INoteParentType.trip:
        return trip?.completedAt == null;
      case INoteParentType.cruise:
        return cruise?.completedAt == null;
      default:
        return true;
    }
  }, [parentType, dayIsCurrentTrip, trip, cruise]);
  const formEditable = useMemo(() => shipCloudEditable || (app.editable && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip)), []);
  const noteParent = useMemo(() => NoteParent(parentType, cruise, day, trip), [parentType, cruise, day, trip]);
  const shipNotes = useQuery(store, SchemaEntities.Note, { shipId: ship.id });
  useRetainedEntities(
    [SchemaEntities.Note, SchemaEntities.NoteAttachment],
    noteParent?.id !== undefined,
    noteParent?.id,
  );
  // `NoteTabs` rebuilds the `notes` array on every render, and applying a bundle
  // notifies the store, which re-renders this view -- keying the re-seed on the rows'
  // content rather than on the array's identity stops that feeding back into itself.
  const seedRows = useMemo(() => nestedNoteRows(notes), [notes]);
  const seedSignature = useMemo(() => JSON.stringify(seedRows), [seedRows]);
  const seedRowsRef = useRef(seedRows);
  seedRowsRef.current = seedRows;
  useEffect(() => {
    store.applyBundle({ entities: seedRowsRef.current });
  }, [seedSignature, store]);
  const seededNoteIds = useMemo(() => new Set(notes.map((note) => note.id)), [notes]);
  const storedFoldNotes = shipNotes.filter((note) => {
    if (seededNoteIds.has(note.id)) return true;
    if (noteParent?.id === undefined) return false;
    switch (parentType) {
      case INoteParentType.cruise:
        return note.cruiseId === noteParent.id && note.tripId == undefined && note.dayId == undefined;
      case INoteParentType.trip:
        return note.tripId === noteParent.id && note.dayId == undefined;
      case INoteParentType.day:
        return note.dayId === noteParent.id;
    }
  });
  const canonicalNotes = storedFoldNotes.length > 0 || notes.length === 0 ? storedFoldNotes : notes;
  // #1137: a blank/backfilled day (`!noteParent.id`) has no Day row yet, so it can
  // never have any notes of its own -- render an uncreated draft instead of
  // eagerly auto-creating one (see the effect below and `NoteView`'s
  // `manualSave={!note}`). Only a day parent can ever be blank; trip/cruise notes
  // keep today's eager auto-create unchanged.
  const blank = !noteParent?.id;
  const typedNotes = useMemo(() => canonicalNotes.filter((note) => note.noteType === noteType), [canonicalNotes, noteType]);
  const ownedNotes = useMemo(() => typedNotes.filter((n) => NoteOwnedByParent(parentType, n)), [typedNotes, parentType]);
  const parentNotes = useMemo(() => typedNotes.filter((n) => !NoteOwnedByParent(parentType, n)), [typedNotes, parentType]);

  const addNewNote = useCallback(async () => {
    if (!noteParent) {
      return undefined;
    }
    const result = await writers.createNote(parentType, noteParent.id, { note: "", noteType: noteType, title: "" });
    if (result && result.note) {
      return result.note;
    }
    return undefined;
  }, [noteParent, noteType, parentType, writers]);

  useEffect(() => {
    if (blank) {
      // #1137: creation is deferred to the user's explicit save on the draft
      // NoteView rendered below -- never fire just from opening this tab.
      return;
    }
    if (ownedNotes.length < 1 && autoCreatingNote == 0 && autoCreateNoteTypes.indexOf(noteType) >= 0 && formEditable && noteParent) {
      const autoCreate = async () => {
        setAutoCreatingNote(1);
        const note = await addNewNote();
        if (note) {
          logger.debug("created blank note", noteType, note);
          setAutoCreatingNote(2);
        }
      };
      autoCreate();
    }
  }, [ownedNotes, autoCreatingNote, addNewNote, blank, formEditable, noteParent, noteType]);

  if (autoCreatingNote == 1) {
    return (
      <div className="NotesView">
        <Loading />
      </div>
    );
  }

  if (blank) {
    return (
      <div className="NotesView">
        {formEditable && <NoteView editable={true} isCurrentTrip={isCurrentTrip} note={undefined} noteType={noteType} parentType={parentType} />}
      </div>
    );
  }

  return (
    <div className="NotesView">
      {ownedNotes.map((note) => <NoteView key={note.id} editable={true} isCurrentTrip={isCurrentTrip} note={note} noteType={noteType} parentType={parentType} />)}
      {formEditable && singleNoteTypes.indexOf(noteType) < 0 && <button className="button" onClick={addNewNote}>{t("note_add_new")}</button>}
      {parentNotes.length > 0 && (
        <details>
          <summary>{t("show_notes", { count: parentNotes.length, type: parentType == INoteParentType.trip ? t("note_parent_type_day") : "" })}</summary>
          <hr />
          {parentNotes.map((note) => <NoteView key={note.id} editable={false} isCurrentTrip={isCurrentTrip} note={note} noteType={noteType} parentType={parentType} />)}
        </details>
      )}
    </div>
  );
}
